import _ from "lodash";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MDBCardBody } from "mdbreact";

import { useAuth } from "hooks/useAuth";
import useModal from "hooks/useModal";
import { fetchCustomProperties } from "actions/properties/customPropertyActions";

import Table from "components/common/tables/Table";
import Modal from "components/common/Modal";
import EmptyList from "components/common/EmptyList";
import SectionHeaderMenu from "components/nav/SectionHeaderMenu";
import CustomPropertyCreate from "./CustomPropertyCreate";
import CustomPropertyUpdate from "./CustomPropertyUpdate";
import CustomPropertyDelete from "./CustomPropertyDelete";

const CustomPropertyList = () => {
  // hooks
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    footer,
    setFooter,
    actions,
    setActions,
    selected,
    setSelected,
    params,
    setParams,
    setModalState
  } = useModal();
  const auth = useAuth();
  const dispatch = useDispatch();

  // redux hooks
  const { propertiesAvailable, lastAction } = useSelector(
    state => state.customProperties
  );

  // local state
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: "#",
        field: "id",
        sort: "desc"
      },
      {
        label: "Name",
        field: "name",
        sort: "desc",
        minimal: "lg"
      },
      {
        label: "Type",
        field: "property_type.name",
        sort: "desc"
      },
      {
        label: "Date Created (UTC)",
        field: "created_at",
        date: true,
        sort: "desc"
      },
      {
        label: "",
        field: "buttons",
        sort: "disabled",
        minimal: "lg"
      }
    ]
  });

  const toggleModal = useCallback(
    (e, type, item) => {
      if (e) e.preventDefault();

      if (type === "new") {
        setHeaderText("New Custom Property");
        setFooter(true);
        setActions([
          {
            text: "Submit",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
      } else if (type === "edit") {
        setHeaderText("Edit Custom Property");
        setFooter(true);
        setActions([
          {
            text: "Update",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      } else if (type === "delete") {
        setHeaderText("Delete Custom Property");
        setFooter(true);
        setActions([
          {
            text: "Delete",
            color: "danger",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      }
      if (type) {
        setModalState(true);
      } else {
        setModalState(false);
      }
    },
    [formId, setActions, setFooter, setHeaderText, setModalState, setSelected]
  );

  // use effect setups
  useEffect(() => {
    dispatch(fetchCustomProperties());
  }, [dispatch]);

  const fetchTableLink = useCallback((type, item) => {
    switch (type) {
      case "list":
        return `/admin/properties/${item.id}`;
      default:
        return null;
    }
  }, []);

  const sectionHeaderMenu = {
    order: 2,
    show: true,
    linkItems: [
      {
        name: "Create",
        url: `/admin/properties`,
        exact: true,
        additionalProps: {
          active: true,
          onClick: e => toggleModal(e, "new"),
          className: "btn-sm"
        }
      }
    ]
  };

  const renderModalBody = () => {
    if (headerText === "New Custom Property") {
      return (
        <CustomPropertyCreate
          formId={formId}
          params={params}
          setParams={setParams}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Edit Custom Property") {
      return (
        <CustomPropertyUpdate
          formId={formId}
          params={params}
          setParams={setParams}
          selected={selected}
          setSelected={setSelected}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Delete Custom Property") {
      return (
        <CustomPropertyDelete
          formId={formId}
          selected={selected}
          closeModal={() => setModalState(false)}
        />
      );
    }

    return "";
  };

  const renderTable = () => {
    if (_.size(propertiesAvailable)) {
      return (
        <Table
          section="Custom Property"
          listTitle="Manage Custom List Items"
          tableData={tableData}
          setTableData={setTableData}
          rowData={propertiesAvailable}
          sort={["id", "desc"]}
          fetchTableLink={fetchTableLink}
          toggleModal={toggleModal}
          updatedState={lastAction}
          downloadableCSV
          editableListButton
          editButton
          deleteButton
        />
      );
    } else if (_.isEmpty(propertiesAvailable)) {
      return (
        <EmptyList
          name="property"
          namePlural="properties"
          userRole={auth?.user?.user_level}
          createElement={
            <span className="link" onClick={e => toggleModal(e, "new")}>
              Create
            </span>
          }
        />
      );
    }

    return "";
  };

  return (
    <>
      <SectionHeaderMenu menuData={sectionHeaderMenu} />
      <MDBCardBody>{renderTable()}</MDBCardBody>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        headerText={headerText}
        includeFooter={footer}
        actionButtons={actions}
      >
        {renderModalBody()}
      </Modal>
    </>
  );
};

export default CustomPropertyList;
